import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/redux/Store';
import './MigrationBlocker.scss';

/**
 * Post-mainnet-cutover blocker.
 *
 * Shown when the authenticated user carries `needsMainnetMint: true` —
 * set by the AuthController (login / /me / wallet-login) when the
 * user's `nft_profile` row exists but `profile_id` is NULL. The NULL is
 * written by `server/scripts/mainnet-cutover-wipe-testnet-state.ts` at
 * mainnet launch: identity + display + social graph carry over, but the
 * user's on-chain NFT (testnet profile_id + sft_nonce) is severed
 * because the mainnet contract has no knowledge of testnet state.
 *
 * The user re-mints on the mainnet contract via the `/create-profile`
 * page (rendered in "conversion mode" when needsMainnetMint is true —
 * pre-fills displayName/bio/avatar/location from the existing profile
 * so the user doesn't re-enter data they already gave us). That page's
 * KleverService flow builds an unsigned tx, the wallet signs, the
 * backend confirm write repopulates `nft_profile.profile_id` and
 * `sft_nonce`; on the next /me hydration `needsMainnetMint` flips back
 * to false and this component unmounts.
 *
 * The blocker stays visible over /create-profile as an information
 * layer (users may need the "what carries over / what resets" context
 * while filling out the form). The CTA becomes a dismiss button on
 * that page — dismissal is local (component state), not persistent,
 * so a page refresh brings the blocker back until the mint completes.
 */
const MigrationBlocker: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const needsMainnetMint = useAppSelector((state) => state.auth.user?.needsMainnetMint);
  const [dismissed, setDismissed] = useState(false);

  if (!needsMainnetMint) return null;
  if (dismissed) return null;

  const onCreateProfilePage = location.pathname.startsWith('/create-profile');

  const handleCta = () => {
    if (onCreateProfilePage) {
      setDismissed(true);
    } else {
      navigate('/create-profile');
    }
  };

  return (
    <div className="migration-blocker__overlay" role="dialog" aria-modal="true" aria-labelledby="migration-blocker-title">
      <div className="migration-blocker__modal">
        <div className="migration-blocker__header">
          <div className="migration-blocker__icon">🚀</div>
          <h2 id="migration-blocker-title">Welcome to Klyx Mainnet</h2>
          <p>Thanks for being part of the testnet — one more step before you continue.</p>
        </div>

        <div className="migration-blocker__content">
          <section>
            <h3>What's carried over</h3>
            <ul>
              <li>Your username, avatar, and bio</li>
              <li>Followers, follows, favourites</li>
              <li>Your posts, comments, and messages</li>
              <li>Beta-survivor achievement (awarded once you re-mint)</li>
            </ul>
          </section>

          <section>
            <h3>What resets</h3>
            <ul>
              <li>Your on-chain profile NFT (testnet SFT is not valid on mainnet)</li>
              <li>Trade history + spotlight receipts (testnet KLV amounts don't translate)</li>
              <li>Reputation scores (rebuild fresh on mainnet activity)</li>
            </ul>
          </section>

          <section className="migration-blocker__price-note">
            <p>
              Your new profile mints at the default price on the mainnet contract.
            </p>
          </section>
        </div>

        <div className="migration-blocker__footer">
          <button
            type="button"
            className="migration-blocker__cta"
            onClick={handleCta}
          >
            {onCreateProfilePage ? 'Got it, continue' : 'Re-mint my profile'}
          </button>
          <p className="migration-blocker__hint">
            {onCreateProfilePage
              ? 'The form below is pre-filled with your existing profile — just review and confirm.'
              : "You'll sign one transaction with your Klever wallet."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MigrationBlocker;
